import { AreaSizePredicate } from './text.controller';
import { TextControllerMethods } from './text.controller.methods';
import { Text, TextChunk, TextChunkKind } from './text.model';

const SEARCH_STEPS = 20;
const MAX_DOUBLINGS = 30;

function isAutomaticLineBreak(chunk: TextChunk): boolean
{
  return chunk.kind === TextChunkKind.Whitespace &&
      !!chunk.breaksLine &&
      chunk.isUserInput === false;
}

export function isAutomaticWordBreak(chunk: TextChunk): boolean
{
  return isAutomaticLineBreak(chunk) && chunk.breaksWord === false;
}

function automaticWordBreak(): TextChunk
{
  return {
    kind: TextChunkKind.Whitespace,
    content: '',
    breaksLine: true,
    breaksWord: false,
    isUserInput: false,
  };
}

export function withoutAutomaticLineBreaks(
  text: Array<TextChunk>): Array<TextChunk>
{
  const result: Array<TextChunk> = [];
  let joinsWord = false;

  for (const chunk of text) {
    if (isAutomaticWordBreak(chunk)) {
      joinsWord = true;
      continue;
    }

    const previous = result[result.length - 1];
    if (isAutomaticLineBreak(chunk)) {
      result.push(
          {...chunk, content: ' ', breaksLine: false, isUserInput: true});
    }
    else if (joinsWord && previous && previous.kind === TextChunkKind.Word &&
             chunk.kind === TextChunkKind.Word) {
      result[result.length - 1] = {
        ...previous,
        content: (previous.content || '') + (chunk.content || ''),
      };
    }
    else {
      result.push(chunk);
    }
    joinsWord = false;
  }

  return result;
}

function lineWidth(model: Text, line: Array<TextChunk>): number
{
  return TextControllerMethods.getTextUsageArea({...model, text: line}).width;
}

function wrapped(
  model: Text,
  text: Array<TextChunk>,
  maxWidth: number,
  breakWords: boolean): Array<TextChunk>
{
  const result: Array<TextChunk> = [];
  let lineStart = 0;
  let lastWhitespace = -1;
  const lineIsTooWide = (): boolean =>
      lineWidth(model, result.slice(lineStart)) > maxWidth;

  for (const chunk of withoutAutomaticLineBreaks(text)) {
    if (chunk.breaksLine) {
      lineStart = result.length;
      lastWhitespace = -1;
    }

    result.push(chunk);

    if (chunk.kind === TextChunkKind.Whitespace && !chunk.breaksLine) {
      lastWhitespace = result.length - 1;
      continue;
    }

    if (chunk.kind !== TextChunkKind.Word || !lineIsTooWide()) {
      continue;
    }

    if (lastWhitespace !== -1) {
      result[lastWhitespace] = {
        ...result[lastWhitespace],
        content: '',
        breaksLine: true,
        isUserInput: false,
      };
      lineStart = lastWhitespace;
      lastWhitespace = -1;
    }

    while (breakWords && lineIsTooWide()) {
      const word = result.pop();
      const characters = Array.from(word.content || '');
      if (characters.length < 2) {
        result.push(word);
        break;
      }

      const lineFitsWith = (count: number): boolean => lineWidth(model, [
        ...result.slice(lineStart),
        {...word, content: characters.slice(0, count).join('')},
      ]) <= maxWidth;

      let count = 1;
      while (count < characters.length - 1 && lineFitsWith(count + 1)) {
        count++;
      }

      result.push(
          {...word, content: characters.slice(0, count).join('')},
          automaticWordBreak());
      lineStart = result.length - 1;
      result.push({...word, content: characters.slice(count).join('')});
    }
  }

  return result;
}

function laidOutAt(model: Text, width: number, breakWords: boolean): Text
{
  const text = wrapped(model, model.text, width, breakWords);
  const usage = TextControllerMethods.getTextUsageArea({...model, text});

  return {
    ...model,
    text,
    availableArea: {
      width,
      height: Math.max(usage.height, model.leading * model.fontSize),
    },
  };
}

export function getMinimalWidth(model: Text): number
{
  return TextControllerMethods.getTextUsageArea(
      {...model, text: wrapped(model, model.text, 0, false)}).width;
}

function bisectedBorder(
  satisfied: number,
  unsatisfied: number,
  monotonicPredicate: (value: number) => boolean): number
{
  for (let step = 0; step < SEARCH_STEPS; step++) {
    const middle = (satisfied + unsatisfied) / 2;
    if (monotonicPredicate(middle)) {
      satisfied = middle;
    }
    else {
      unsatisfied = middle;
    }
  }

  return satisfied;
}

function doubledWhile(
  value: number, predicate: (value: number) => boolean): number
{
  for (let i = 0; i < MAX_DOUBLINGS && predicate(value); i++) {
    value *= 2;
  }

  return value;
}

export function laidOutInArea(
  model: Text, allowsResizeTo: AreaSizePredicate): Text | undefined
{
  const width = Math.max(model.availableArea.width, getMinimalWidth(model));
  const lineHeight = model.leading * model.fontSize;
  const fitsOneLine = (w: number): boolean =>
      allowsResizeTo({ width: w, height: lineHeight });

  if (!fitsOneLine(0)) {
    return undefined;
  }

  const maxWidth = bisectedBorder(
      0,
      doubledWhile(Math.max(width, lineHeight, 1), fitsOneLine),
      fitsOneLine);

  const fitsAt = (w: number): boolean =>
      allowsResizeTo(laidOutAt(model, w, true).availableArea);

  const preferredWidth = Math.min(width, maxWidth);
  if (fitsAt(preferredWidth)) {
    return laidOutAt(model, preferredWidth, true);
  }
  if (!fitsAt(maxWidth)) {
    return undefined;
  }

  return laidOutAt(
      model, bisectedBorder(maxWidth, preferredWidth, fitsAt), true);
}

export function fittedToArea(
  model: Text,
  allowsResizeTo: AreaSizePredicate,
  minFontSize: number): Text | undefined
{
  const laidOutWith = (fontSize: number): Text | undefined =>
      laidOutInArea({...model, fontSize}, allowsResizeTo);

  const reduction = TextControllerMethods.fontSizeReducedInStepsOfOne(
      model.fontSize, minFontSize, size => laidOutWith(size) !== undefined);

  return reduction.satisfied ? laidOutWith(reduction.fontSize) : undefined;
}

export function largestFittingFontSize(
  model: Text, allowsResizeTo: AreaSizePredicate): number
{
  const fits = (fontSize: number): boolean =>
      laidOutInArea({...model, fontSize}, allowsResizeTo) !== undefined;
  const fontSizeUpperBound = doubledWhile(1, fontSize =>
      allowsResizeTo({ width: 0, height: model.leading * fontSize }));

  const reduction = TextControllerMethods.fontSizeReducedInStepsOfOne(
      fontSizeUpperBound, 1, fits);

  return reduction.satisfied ? reduction.fontSize : 0;
}
